import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { renderToStaticMarkup } from "react-dom/server";
import { useSignal } from "@preact/signals";
import classNames from "classnames";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import merge from "@tinkoff/utils/object/mergeDeep";
import { chartOptions, getChartTimespan } from "../../../lib/chart";
import { useInterval } from "../../../lib/hooks/use-interval";
import ChartTooltip from "../../Tooltip/ChartTooltip";
import ComponentWithData from "../../ComponentWithData";
import Loader from "../../Loader";
import thresholdsTransformer from "../../../lib/transformers/thresholds-transformer";
import useMessageChannel from "../../../lib/hooks/use-message-channel";
import { EVENT_CHART_ACTION } from "../../ChartOptions";
import { exportChartAsCsv, exportChartAsImage } from "../../../lib/charts/export";
import addTrendSeries from "../../../lib/transformers/add-trend-series";
import { isDebugMode } from "../../../lib/utils";
export default function BaseChart(props) {
    const staticData = props.data;
    const isStaticData = staticData.length && staticData[0].data;
    const isMapChart = ["map", "mapbubble"].includes(props.type);
    const constructorType = isMapChart ? "mapChart" : "chart";
    const chartRef = useRef(undefined);
    const xAxisOverride = useSignal(null);
    const channel = useMessageChannel();
    useEffect(() => {
        return () => {
            // Unsubscribe all message channel listeners to avoid duplicates on re-renders
            channel.unsubscribeAll();
            // Destroy any existing tooltips before the chart is unmounted
            if (chartRef.current && chartRef.current.scTooltip) {
                chartRef.current.scTooltip.destroy();
            }
        };
    }, [channel]);
    const onChartCreated = (chart) => {
        var _a;
        if ((_a = chart.userOptions.exporting) === null || _a === void 0 ? void 0 : _a.type) {
            // Ignore charts that are being exported. They will be discarded after the export has
            // completed. We do not want to keep a reference to them otherwise they will not be
            // garbage collected.
            return;
        }
        if (props.onChartCreated) {
            props.onChartCreated(chart);
        }
        chartRef.current = chart;
    };
    // Subscribe to chart action events
    channel.subscribe(EVENT_CHART_ACTION, (action) => {
        // Only handle actions for charts that match the action chartId
        if (chartRef.current && action.chartId === props.chartId) {
            const exportedChartTitle = props.chartName || props.chartId;
            const exportFileName = props.chartFileName || props.chartId;
            if (action.type === "download-csv") {
                exportChartAsCsv(chartRef.current, exportFileName);
            }
            else if (action.type === "download-image") {
                exportChartAsImage(chartRef.current, exportFileName, exportedChartTitle);
            }
        }
    });
    const getHighChartsOptions = (series) => {
        var _a;
        const { enableMultipleYAxes, transformers = [] } = props;
        const queryDialect = ((_a = series[0]) === null || _a === void 0 ? void 0 : _a.queryDialect) || "default";
        if (props.enableThresholds) {
            transformers.push(thresholdsTransformer);
        }
        if (window.bTrendsVisible) {
            transformers.push(addTrendSeries);
        }
        let data = series.filter((seriesObj) => {
            return seriesObj && seriesObj.data;
        });
        if (transformers.length) {
            // Apply all the transformers from left to right
            data = transformers.reduce((result, transformer) => transformer(result), data);
        }
        let options = chartOptions(data);
        if (props.height) {
            options = merge(options, { chart: { height: props.height } });
        }
        if (props.hideLegend) {
            options = merge(options, { legend: { enabled: false } });
        }
        options = merge(options, getTooltipOptions(props.tooltip || ChartTooltip));
        if (enableMultipleYAxes) {
            options = multipleYAxisSetup(options, data);
        }
        if (props.finalChartOptions) {
            options = props.finalChartOptions(options);
        }
        if (xAxisOverride.value) {
            options = merge(options, { xAxis: xAxisOverride.value });
        }
        options = merge(options, {
            chart: {
                className: classNames(
                // Always add a QA class to the chart container for automated testing
                `qa-highcharts-${props.type}`, {
                    [`query-dialect-${queryDialect}`]: isDebugMode(),
                }),
            },
        });
        return options;
    };
    useInterval(() => {
        if (chartRef.current) {
            const timespan = getChartTimespan(chartRef.current);
            const now = Date.now();
            xAxisOverride.value = {
                min: now - timespan,
                max: now,
            };
        }
    }, props.xAxisTickInterval);
    if (!isStaticData) {
        const loaderHeight = props.loaderHeight || props.height || 400;
        return (_jsx(ComponentWithData, { data: props.data, dataRefreshInterval: props.dataRefreshInterval, loading: _jsx(Loader, { height: loaderHeight }), component: (series) => (_jsx(HighchartsReact, { highcharts: Highcharts, constructorType: constructorType, options: getHighChartsOptions(series), callback: onChartCreated, updateArgs: [true, false, false] })) }));
    }
    return (_jsx(HighchartsReact, { highcharts: Highcharts, constructorType: constructorType, options: getHighChartsOptions(staticData), callback: onChartCreated }));
}
/**
 * Get the Highcharts options required to render a tooltip component
 */
export function getTooltipOptions(Tooltip) {
    return {
        chart: {
            allowMutatingData: false,
        },
        plotOptions: {
            series: {
                point: {
                    events: {
                        click: function (event) {
                            const point = this;
                            const series = point.series;
                            const chart = series.chart;
                            const tooltipId = `speedcurve-chart-tooltip-${chart.index}`;
                            function removeTooltip() {
                                if (chart.scTooltip) {
                                    chart.scTooltip.destroy();
                                }
                            }
                            const tooltipNode = _jsx(Tooltip, { point: point });
                            // Create a new tooltip object attached to the current chart. By
                            // doing this, we prevent Highcharts from attaching all of the default
                            // event handlers so that we have complete control over when the
                            // tooltip is shown and hidden.
                            chart.scTooltip = new Highcharts.Tooltip(chart, {
                                enabled: true,
                                outside: true,
                                useHTML: true,
                                shadow: false,
                                backgroundColor: "rgba(0, 0, 0, 0)",
                                borderWidth: 0,
                                padding: 0,
                                style: {
                                    pointerEvents: "auto",
                                },
                                formatter: function () {
                                    // Rendering a tooltip is a multi-step process. Step 1 is to set
                                    // up the formatter to render a static tooltip so that Highcharts
                                    // can figure out the dimensions of the container.
                                    //
                                    // It seems wasteful to render the tooltip twice, but if we don't
                                    // do this, Highcharts might position the tooltip off-screen because
                                    // it thinks the container is smaller than it really is.
                                    return `<div id="${tooltipId}">${renderToStaticMarkup(tooltipNode)}</div>`;
                                },
                            }, chart.pointer);
                            // Step 2 is to tell Highcharts to render the tooltip on the point that
                            // was clicked
                            chart.scTooltip.refresh(point, event);
                            // Step 3 is to create a root in the tooltip container, and render a fully
                            // hydrated tooltip over it
                            const root = createRoot(document.getElementById(tooltipId));
                            root.render(tooltipNode);
                            const removeTooltipOnClickOutside = (event) => {
                                // Remove the tooltip if the user clicks anywhere outside of the tooltip
                                if (event.target instanceof Element && !event.target.closest(`#${tooltipId}`)) {
                                    document.removeEventListener("click", removeTooltipOnClickOutside);
                                    removeTooltip();
                                }
                            };
                            document.addEventListener("click", removeTooltipOnClickOutside, { capture: true });
                        },
                    },
                },
            },
        },
    };
}
// Setup multiple y Axis if we have series with different types.
function multipleYAxisSetup(highChartsOptions, data) {
    const seriesTypes = [];
    data.forEach((dataSeries) => {
        if (dataSeries.yAxis === "sample-size") {
            // Don't add a sample size Y axis. That is handled by the chart components.
            return;
        }
        let valType = dataSeries.metricType ? dataSeries.metricType : "";
        const color = dataSeries.color ? dataSeries.color : "";
        //Differentiate seconds metrics from milliseconds metrics.
        if (dataSeries.metricType === "time") {
            valType = dataSeries.metricType + dataSeries.suffix;
        }
        const metricType = seriesTypes.find((value) => {
            return value.type === valType;
        });
        if (!metricType) {
            let units = "";
            if (typeof dataSeries.suffix !== "undefined") {
                units = dataSeries.suffix;
            }
            else if (typeof dataSeries.xAxisUnits !== "undefined") {
                units = dataSeries.xAxisUnits;
            }
            seriesTypes.push({ type: valType, count: 1, color: color, units: units });
        }
        else {
            metricType.count++;
        }
    });
    data.forEach((dataSeries) => {
        if (dataSeries.metricType === "time") {
            dataSeries.yAxis = dataSeries.metricType + dataSeries.suffix;
        }
        else {
            dataSeries.yAxis = dataSeries.metricType;
        }
    });
    const yAxis = [];
    // When we add new Y axes, we want to inherit the properties of the base Y axis.
    let baseYAxis = {};
    if (!Array.isArray(highChartsOptions.yAxis)) {
        baseYAxis = highChartsOptions.yAxis || {};
    }
    else if (highChartsOptions.yAxis.length === 1) {
        // If the yAxis options are defined as an array, only treat them as the "base" if there is
        // just one of them.
        baseYAxis = highChartsOptions.yAxis[0];
    }
    seriesTypes.forEach((seriesType, index) => {
        let labelColor = seriesType.color;
        if (seriesType.count > 1 || seriesTypes.length === 1) {
            labelColor = "var(--high-emphasis)";
        }
        const oppositeSide = index !== 0;
        let ceilingVal = undefined;
        if (seriesType.type === "percent") {
            ceilingVal = 100;
        }
        const thisYAxis = merge(baseYAxis, {
            id: seriesType.type,
            labels: {
                style: {
                    color: labelColor,
                },
                format: `{value}${seriesType.units}`,
            },
            opposite: oppositeSide,
            ceiling: ceilingVal,
            reversedStacks: false,
        });
        if (seriesType.type === "score") {
            thisYAxis.max = 100;
        }
        yAxis.push(thisYAxis);
    });
    highChartsOptions.yAxis = yAxis;
    return highChartsOptions;
}
